import { useEffect, useState } from 'react';
import { trendingApi } from './api';

// Bucket for the trending rank call -- must match the sectionType TrendingSection already
// uses for /how-it-works/* pages (see HowItWorksPage.jsx / HowItWorksSectionPage.jsx), so
// popularity here reflects the same recorded PageVisitTracker visits, not a separate count.
const SECTION_TYPE = 'how_it_works';

const STOPWORDS = new Set([
  'the', 'and', 'for', 'with', 'that', 'this', 'from', 'into', 'onto', 'used', 'uses',
  'using', 'works', 'work', 'how', 'does', 'what', 'where', 'which', 'when', 'their',
  'these', 'those', 'than', 'then', 'have', 'has', 'are', 'was', 'were', 'been', 'being',
  'also', 'such', 'each', 'other', 'more', 'most', 'some', 'while', 'through', 'between',
  'across', 'over', 'under', 'about', 'both', 'can', 'its', 'via',
]);

// Topics carry no free-form tags field (only `category`/`subcategory`), so the closest
// available proxy for "overlapping tags/keywords" is the significant words shared between
// two topics' title + short_description text.
const keywordsOf = (topic) => {
  const text = `${topic.title} ${topic.short_description || ''}`.toLowerCase();
  const words = text.match(/[a-z][a-z-]{3,}/g) || [];
  return new Set(words.filter((w) => !STOPWORDS.has(w)));
};

const keywordOverlapScore = (a, b) => {
  if (a.size === 0 || b.size === 0) return 0;
  let shared = 0;
  for (const w of a) if (b.has(w)) shared += 1;
  return shared / Math.min(a.size, b.size);
};

// Scores `candidates` against `current` in priority order: (1) same category/subcategory,
// (2) keyword overlap (the tag/keyword + conceptual-similarity proxy -- there's no separate
// structured field to tell those two apart). Popularity (priority 4) isn't scored here since
// it needs an async call; see `useRelatedTopics`, which applies it only as a tiebreaker.
export const scoreRelatedCandidates = (current, candidates) => {
  const currentWords = keywordsOf(current);
  return candidates.map((topic) => {
    let tier = 0;
    if (current.category && topic.category === current.category) {
      tier = current.subcategory && topic.subcategory === current.subcategory ? 2 : 1;
    }
    return { topic, tier, keywordScore: keywordOverlapScore(currentWords, keywordsOf(topic)) };
  });
};

// Full ranking of `allTopics` against `current`, best match first (current topic itself
// excluded). `popularityRank` is a Map of topic_id -> rank position from the trending
// endpoint (lower = more popular, missing = never visited => sorts last); ties beyond that
// fall back to most-recently-updated for a fully deterministic order.
export const rankRelatedTopics = (current, allTopics, popularityRank = new Map()) => {
  const scored = scoreRelatedCandidates(
    current,
    allTopics.filter((t) => t.topic_id !== current.topic_id)
  );
  return scored
    .sort((a, b) => {
      if (b.tier !== a.tier) return b.tier - a.tier;
      if (b.keywordScore !== a.keywordScore) return b.keywordScore - a.keywordScore;
      const rankA = popularityRank.has(a.topic.topic_id) ? popularityRank.get(a.topic.topic_id) : Infinity;
      const rankB = popularityRank.has(b.topic.topic_id) ? popularityRank.get(b.topic.topic_id) : Infinity;
      if (rankA !== rankB) return rankA - rankB;
      return new Date(b.topic.updated_at || 0) - new Date(a.topic.updated_at || 0);
    })
    .map((s) => s.topic);
};

// Up to `count` topics most related to the topic identified by `topicId`, drawn from the
// already-fetched full catalog (`allTopics`, from `useHowItWorksTopics`) -- same "filter/rank
// the candidate list client-side" pattern as the section/subsection trending pages, so no new
// backend endpoint is needed. Returns [] until the catalog has loaded or `topicId` isn't in it.
export const useRelatedTopics = (topicId, allTopics, count = 3) => {
  const [popularityRank, setPopularityRank] = useState(new Map());
  const current = allTopics.find((t) => t.topic_id === topicId);

  useEffect(() => {
    if (!current || allTopics.length === 0) return undefined;
    let cancelled = false;
    const items = allTopics
      .filter((t) => t.topic_id !== current.topic_id)
      .map((t) => ({ item_id: `/how-it-works/${t.topic_id}`, updated_at: t.updated_at || null }));
    trendingApi
      .rank(SECTION_TYPE, items)
      .then((ids) => {
        if (cancelled) return;
        const rankMap = new Map(ids.map((id, i) => [id.replace('/how-it-works/', ''), i]));
        setPopularityRank(rankMap);
      })
      .catch(() => {
        if (!cancelled) setPopularityRank(new Map());
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.topic_id, allTopics.length]);

  if (!current) return [];
  return rankRelatedTopics(current, allTopics, popularityRank).slice(0, count);
};
